import { OrgChartNodeData, ITreeNode } from '../types/org-chart';
import dagre from '@dagrejs/dagre';
import { MarkerType } from 'reactflow';

/**
 * Converts a flat list of employees into a hierarchical tree structure.
 * Primarily used for identifying levels in the reporting line.
 */
export const buildTree = (nodes: OrgChartNodeData[]): ITreeNode[] => {
  const map: { [key: string]: ITreeNode } = {};
  const roots: ITreeNode[] = [];

  // Initialize map
  nodes.forEach((node) => {
    map[node.id] = { ...node, children: [] };
  });

  // Link children to parents (prefer reporting, fallback to functional)
  nodes.forEach((node) => {
    const parentId = node.reportingManagerId || node.functionalManagerId;
    if (parentId && map[parentId]) {
      map[parentId].children.push(map[node.id]);
    } else {
      roots.push(map[node.id]);
    }
  });

  return roots;
};

export interface LayoutConfig {
  ranker: 'network-simplex' | 'longest-path' | 'tight-tree';
  nodesep: number;
  ranksep: number;
}

/**
 * Advanced layout logic using Dagre for optimal node placement and line routing.
 */
export const getDagreLayout = (
  nodes: OrgChartNodeData[],
  direction: 'TB' | 'LR' = 'TB',
  config?: LayoutConfig
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 300;
  const nodeHeight = 140;

  const {
    ranker = 'tight-tree',
    nodesep = 40,
    ranksep = 140
  } = config || {};

  // Set graph direction and spacing
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep,
    ranksep,
    ranker,
    marginx: 50,
    marginy: 50,
  });



  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre
  nodes.forEach((node) => {
    if (node.reportingManagerId) {
      dagreGraph.setEdge(node.reportingManagerId, node.id, { weight: 10 });
    }
    if (node.functionalManagerId && node.functionalManagerId !== node.reportingManagerId) {
      dagreGraph.setEdge(node.functionalManagerId, node.id, { weight: 1 });
    }
  });

  // Run layout
  dagre.layout(dagreGraph);

  // Identify tree structures to handle orphan placement
  const treeRoots = buildTree(nodes);
  const nodeToRootId = new Map<string, string>();
  const markSubtree = (tn: ITreeNode, rootId: string) => {
    nodeToRootId.set(tn.id, rootId);
    tn.children.forEach(c => markSubtree(c, rootId));
  };
  treeRoots.forEach(r => markSubtree(r, r.id));

  // Find the boundary for Admin nodes and main tree nodes
  let maxAdminY = 0;
  let mainMinX = 0;
  let hasMain = false;

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const rootId = nodeToRootId.get(node.id);
    const root = treeRoots.find(r => r.id === rootId);
    const rootRole = (root?.role || 'Employee').toLowerCase();
    const isMainTree = rootRole === 'admin' || rootRole === 'superadmin';

    if (isMainTree) {
      if (!hasMain || nodeWithPosition.x < mainMinX) {
        mainMinX = nodeWithPosition.x;
        hasMain = true;
      }
    }

    const role = (node.role || 'Employee').toLowerCase();
    if (role === 'admin' || role === 'superadmin') {
      if (nodeWithPosition.y > maxAdminY) {
        maxAdminY = nodeWithPosition.y;
      }
    }
  });

  // Calculate shifts for orphan trees (roots are not admins)
  const orphanRoots = treeRoots.filter(r => {
    const role = (r.role || 'Employee').toLowerCase();
    return role !== 'admin' && role !== 'superadmin';
  });

  const rootShifts = new Map<string, number>();
  let currentOrphanX = (hasMain ? mainMinX : 0) - (nodeWidth + 20); 

  orphanRoots.forEach(root => {
    const pos = dagreGraph.node(root.id);
    // Shift the whole subtree so the root is at currentOrphanX
    rootShifts.set(root.id, currentOrphanX - pos.x);
    currentOrphanX -= (nodeWidth + 20); 
  });

  // Calculate the base offset for non-admin nodes (below admin zone)
  const nonAdminOffset = maxAdminY + nodeHeight; // Tightened vertical gap between zones

  // Group nodes by their final Y coordinate for horizontal overlap resolution
  const nodesByTier: Record<number, any[]> = {};

  const processedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const role = (node.role || 'Employee').toLowerCase();
    const isAdmin = role === 'admin' || role === 'superadmin';

    // Identify if this node belongs to an orphan tree
    const rootId = nodeToRootId.get(node.id);
    const xShift = rootShifts.get(rootId || '') || 0;

    // Admins stay in their Dagre-calculated top positions
    // Others are shifted down to the tree-based section
    const finalY = isAdmin ? nodeWithPosition.y : nodeWithPosition.y + nonAdminOffset;

    const layoutNode = {
      id: node.id,
      data: { ...node },
      position: {
        x: nodeWithPosition.x - nodeWidth / 2 + xShift,
        y: finalY,
      },
      type: 'employeeNode',
      // Store finalY for tier grouping (using a larger bucket to handle minor dagre variations)
      tierKey: Math.round(finalY / 10),
    };

    if (!nodesByTier[layoutNode.tierKey]) nodesByTier[layoutNode.tierKey] = [];
    nodesByTier[layoutNode.tierKey].push(layoutNode);
    return layoutNode;
  });

  // Resolve horizontal overlaps within each vertical level
  const horizontalGap = 60; // Increased gap for premium feel
  Object.values(nodesByTier).forEach(tierNodes => {
    // Sort nodes in this tier by their initial X position
    tierNodes.sort((a, b) => a.position.x - b.position.x);

    // Shift nodes to the right if they overlap
    for (let i = 0; i < tierNodes.length - 1; i++) {
      const current = tierNodes[i];
      const next = tierNodes[i + 1];
      const minXForNext = current.position.x + nodeWidth + horizontalGap;

      if (next.position.x < minXForNext) {
        next.position.x = minXForNext;
      }
    }
  });

  const layoutedEdges: any[] = [];
  nodes.forEach((node) => {
    if (node.reportingManagerId) {
      layoutedEdges.push({
        id: `e-${node.reportingManagerId}-${node.id}`,
        source: node.reportingManagerId,
        target: node.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 14,
          height: 14,
          color: '#3B82F6',
        },
      });
    }
    if (node.functionalManagerId && node.functionalManagerId !== node.reportingManagerId) {
      layoutedEdges.push({
        id: `e-f-${node.functionalManagerId}-${node.id}`,
        source: node.functionalManagerId,
        target: node.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 14,
          height: 14,
          color: '#10B981',
        },
      });
    }
  });

  return { nodes: processedNodes, edges: layoutedEdges };
};

/**
 * Cycle detection: Checks if moving 'nodeId' to be a report of 'targetId' creates a loop.
 */
export const wouldCreateCycle = (
  nodes: OrgChartNodeData[],
  nodeId: string,
  targetId: string
): boolean => {
  if (nodeId === targetId) return true;

  const map = new Map(nodes.map((n) => [n.id, n.reportingManagerId]));
  let current: string | undefined = targetId;
  const visited = new Set<string>();

  let depth = 0;
  while (current && !visited.has(current) && depth < 100) {
    if (current === nodeId) return true;
    visited.add(current);
    current = map.get(current);
    depth++;
  }

  return false;
};
